import React, { useState, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, IconButton,
    TextField, MenuItem, Typography, CircularProgress, Chip,
    Card, CardContent, Grid, Divider, Avatar, Tabs, Tab,
    InputAdornment, Tooltip, Alert, Fade, Slide, Stack,
    TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, TablePagination, TableSortLabel, Paper
} from '@mui/material';
import {
    Send as SendIcon,
    Close as CloseIcon,
    Preview as PreviewIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Subject as SubjectIcon,
    Notes as NotesIcon,
    Add as AddIcon,
    History as HistoryIcon,
    Search as SearchIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import client from '../../../../../API';

const BlastEmail = () => {
    const [open, setOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [senders, setSenders] = useState([]);
    const [emailData, setEmailData] = useState({
        creatorUsername: '',
        subject: '',
        body: '',
        recipients: '',
    });
    const [recipientChips, setRecipientChips] = useState([]);
    const [currentEmail, setCurrentEmail] = useState('');
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('send_date');
    const [order, setOrder] = useState('desc');

    // Fetch all email blasts
    const { data: emailBlasts, isLoading: loadingEmailBlasts, refetch: refetchEmailBlasts } = useQuery(
        'fetchEmailBlasts',
        () => client.creatorConnect.fetchEmailBlasts(),
        {
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    // Fetch sender emails
    const fetchSenders = async () => {
        try {
            setError('');
            const res = await client.creatorConnect.getAuthorizedSenders();
            setSenders(res);
        } catch (err) {
            console.error('Error fetching sender emails:', err);
            setError('Failed to load sender emails. Please try again later.');
        }
    };

    // Mutation to send bulk email
    const sendBulkEmailMutation = useMutation(
        (emailData) => client.creatorConnect.sendBulkEmail(emailData),
        {
            onSuccess: () => {
                setError('');
                handleClose();
                refetchEmailBlasts();
                // Success notification
                setTimeout(() => {
                    alert('Emails sent successfully!');
                }, 300);
            },
            onError: (err) => {
                console.error('Error sending emails:', err);
                setError('Failed to send emails. Please check the details and try again.');
            },
        }
    );

    const handleOpen = () => {
        setOpen(true);
        fetchSenders();
    };

    const handleClose = () => {
        setOpen(false);
        setEmailData({
            creatorUsername: '',
            subject: '',
            body: '',
            recipients: '',
        });
        setRecipientChips([]);
        setCurrentEmail('');
        setSenders([]);
        setError('');
    };

    const handlePreviewOpen = () => {
        if (!emailData.subject || !emailData.body) {
            setError('Please add subject and body to preview.');
            return;
        }
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEmailData({ ...emailData, [name]: value });
        if (error) setError('');
    };

    const handleAddEmail = () => {
        const email = currentEmail.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (email && emailRegex.test(email) && !recipientChips.includes(email)) {
            setRecipientChips([...recipientChips, email]);
            setCurrentEmail('');
        } else if (!emailRegex.test(email)) {
            setError('Please enter a valid email address.');
        } else if (recipientChips.includes(email)) {
            setError('Email already added.');
        }
    };

    const handleDeleteChip = (emailToDelete) => {
        setRecipientChips(recipientChips.filter((email) => email !== emailToDelete));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleSendEmail = () => {
        const { creatorUsername, subject, body } = emailData;

        if (!creatorUsername || !subject || !body) {
            setError('Please fill in all required fields.');
            return;
        }

        if (recipientChips.length === 0) {
            setError('Please add at least one recipient.');
            return;
        }

        const styledBody = body.replace(/\n/g, '<br />');

        sendBulkEmailMutation.mutate({
            creator_username: creatorUsername,
            subject,
            body: styledBody,
            recipients: recipientChips,
        });
    };

    // Table sorting and filtering
    const filteredBlasts = useMemo(() => {
        if (!emailBlasts?.email_blasts) return [];
        
        return emailBlasts.email_blasts.filter((blast) =>
            blast.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blast.sender?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blast.body?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [emailBlasts, searchTerm]);

    const sortedBlasts = useMemo(() => {
        const blasts = [...filteredBlasts];
        blasts.sort((a, b) => {
            if (orderBy === 'send_date') {
                return order === 'asc' 
                    ? new Date(a.send_date) - new Date(b.send_date)
                    : new Date(b.send_date) - new Date(a.send_date);
            }
            return order === 'asc'
                ? (a[orderBy] || '').toString().localeCompare((b[orderBy] || '').toString())
                : (b[orderBy] || '').toString().localeCompare((a[orderBy] || '').toString());
        });
        return blasts;
    }, [filteredBlasts, order, orderBy]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const selectedSender = senders.find(s => s.username === emailData.creatorUsername);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={6}>
                        <Typography 
                            variant="h4" 
                            fontWeight={700}
                            sx={{ 
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Creator Email Campaigns
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Send targeted emails from Parnter Creators
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md="auto">
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleOpen}
                            sx={{
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                boxShadow: '0 3px 15px rgba(33, 150, 243, 0.3)',
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                                    transform: 'translateY(-2px)',
                                    transition: 'all 0.3s ease'
                                }
                            }}
                        >
                            Create Campaign
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Email Composer Dialog */}
            <Dialog 
                open={open} 
                onClose={handleClose} 
                maxWidth="md" 
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <Box sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 3,
                    position: 'relative'
                }}>
                    <Typography variant="h5" fontWeight={600} color="white">
                        New Email Campaign
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mt: 0.5 }}>
                        Compose and send your message
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 4 }}>
                    {error && (
                        <Fade in={Boolean(error)}>
                            <Alert 
                                severity="error" 
                                sx={{ mb: 3 }}
                                onClose={() => setError('')}
                            >
                                {error}
                            </Alert>
                        </Fade>
                    )}

                    <Grid container spacing={3}>
                        {/* Sender Selection */}
                        <Grid item xs={12}>
                            <TextField
                                name="creatorUsername"
                                label="From"
                                select
                                fullWidth
                                value={emailData.creatorUsername}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            >
                                {senders.length > 0 ? (
                                    senders.map((sender) => (
                                        <MenuItem key={sender.username} value={sender.username}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                                    {sender.username?.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {sender.username}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {sender.email || 'No Email'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No senders available</MenuItem>
                                )}
                            </TextField>
                        </Grid>

                        {/* Selected Sender Info */}
                        {selectedSender && (
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'primary.50', borderRadius: 2 }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                {selectedSender.username?.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Sending as {selectedSender.username}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {selectedSender.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Recipients */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                Recipients
                            </Typography>
                            <TextField
                                label="Add recipient email"
                                fullWidth
                                value={currentEmail}
                                onChange={(e) => setCurrentEmail(e.target.value)}
                                onKeyPress={handleKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton 
                                                onClick={handleAddEmail}
                                                color="primary"
                                                disabled={!currentEmail.trim()}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                                placeholder="email@example.com"
                            />
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {recipientChips.map((email, index) => (
                                    <Chip
                                        key={index}
                                        label={email}
                                        onDelete={() => handleDeleteChip(email)}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ borderRadius: 2 }}
                                    />
                                ))}
                                {recipientChips.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        No recipients added yet
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {/* Subject */}
                        <Grid item xs={12}>
                            <TextField
                                name="subject"
                                label="Subject"
                                fullWidth
                                value={emailData.subject}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SubjectIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                                placeholder="Enter email subject"
                            />
                        </Grid>

                        {/* Body */}
                        <Grid item xs={12}>
                            <TextField
                                name="body"
                                label="Message"
                                multiline
                                rows={8}
                                fullWidth
                                value={emailData.body}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                            <NotesIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                                placeholder="Type your message here..."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button 
                        onClick={handleClose}
                        sx={{ 
                            textTransform: 'none',
                            px: 3,
                            borderRadius: 2 
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreviewOpen}
                        sx={{ 
                            textTransform: 'none',
                            px: 3,
                            borderRadius: 2 
                        }}
                    >
                        Preview
                    </Button>
                    <Button
                        onClick={handleSendEmail}
                        variant="contained"
                        startIcon={sendBulkEmailMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        disabled={sendBulkEmailMutation.isLoading}
                        sx={{
                            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                            textTransform: 'none',
                            px: 4,
                            borderRadius: 2,
                            fontWeight: 600
                        }}
                    >
                        {sendBulkEmailMutation.isLoading ? 'Sending...' : 'Send Campaign'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Preview Dialog */}
            <Dialog 
                open={previewOpen} 
                onClose={handlePreviewClose} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <Box sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 3,
                    position: 'relative'
                }}>
                    <Typography variant="h5" fontWeight={600} color="white">
                        Email Preview
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mt: 0.5 }}>
                        Review before sending
                    </Typography>
                    <IconButton
                        onClick={handlePreviewClose}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 4, bgcolor: '#f5f5f5' }}>
                    <Card sx={{ maxWidth: 650, mx: 'auto', borderRadius: 3, overflow: 'hidden' }}>
                        <Box
                            dangerouslySetInnerHTML={{
                                __html: `
                                    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background: #ffffff;">
                                        <div style="text-align: center; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; padding: 40px 20px;">
                                            <div style="width: 100px; height: 100px; background: rgba(255,255,255,0.2); border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; font-size: 48px; font-weight: bold;">
                                                ${emailData.creatorUsername?.charAt(0).toUpperCase() || 'B'}
                                            </div>
                                            <h1 style="margin: 0; font-size: 28px; font-weight: 700;">${emailData.subject || 'Email Subject'}</h1>
                                        </div>
                                        <div style="padding: 40px 30px; line-height: 1.8; color: #333333; font-size: 16px;">
                                            <p style="margin-top: 0;">${emailData.body.replace(/\n/g, '<br />') || 'Your email message will appear here...'}</p>
                                        </div>
                                        <div style="padding: 30px; background: #f8f9fa; border-top: 1px solid #e0e0e0;">
                                            <p style="margin: 0 0 10px 0; font-style: italic; color: #666; font-size: 15px;">Best regards,</p>
                                            <p style="margin: 0; font-weight: 600; color: #333; font-size: 16px;">${emailData.creatorUsername || 'Creator'} Team</p>
                                        </div>
                                        <div style="text-align: center; padding: 20px; background: #f0f0f0; font-size: 13px; color: #999;">
                                            <p style="margin: 0;">© ${new Date().getFullYear()} Blitz Systems Corp. All rights reserved.</p>
                                            <p style="margin: 10px 0 0 0;">Powered by Blitz Email Campaign Manager</p>
                                        </div>
                                    </div>
                                `
                            }}
                        />
                    </Card>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handlePreviewClose}
                        variant="contained"
                        sx={{ 
                            textTransform: 'none',
                            px: 4,
                            borderRadius: 2 
                        }}
                    >
                        Close Preview
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Blasts History Section */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <HistoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>
                                        Campaign History
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {filteredBlasts.length} {filteredBlasts.length === 1 ? 'campaign' : 'campaigns'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search campaigns..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {loadingEmailBlasts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : sortedBlasts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <EmailIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No campaigns yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'No campaigns match your search.' : 'Create your first email campaign to get started.'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <TableSortLabel
                                                active={orderBy === 'id'}
                                                direction={orderBy === 'id' ? order : 'asc'}
                                                onClick={() => handleSort('id')}
                                            >
                                                ID
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <TableSortLabel
                                                active={orderBy === 'sender'}
                                                direction={orderBy === 'sender' ? order : 'asc'}
                                                onClick={() => handleSort('sender')}
                                            >
                                                Sender
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <TableSortLabel
                                                active={orderBy === 'subject'}
                                                direction={orderBy === 'subject' ? order : 'asc'}
                                                onClick={() => handleSort('subject')}
                                            >
                                                Subject
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Body Preview</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <TableSortLabel
                                                active={orderBy === 'send_date'}
                                                direction={orderBy === 'send_date' ? order : 'asc'}
                                                onClick={() => handleSort('send_date')}
                                            >
                                                Send Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedBlasts
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((blast) => (
                                            <TableRow 
                                                key={blast.id}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8f9fa'
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip 
                                                        label={blast.id} 
                                                        size="small" 
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                                            {blast.sender?.charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {blast.sender}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {blast.subject}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={blast.body}>
                                                        <Typography 
                                                            variant="body2" 
                                                            color="text.secondary"
                                                            sx={{
                                                                maxWidth: 250,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {blast.body?.replace(/<br\s*\/?>/gi, ' ')}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {new Date(blast.send_date).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {blast.sender_jwt_identity}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={sortedBlasts.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </>
                )}
            </Card>
        </Box>
    );
};

export default BlastEmail;